import DoneIcon from '@mui/icons-material/Done';
import WarningIcon from '@mui/icons-material/Warning';
import { Button, SxProps, Theme, useTheme } from '@mui/material';
import { FormikProps } from 'formik';
import { useMemo, useState } from 'react';
import { AnyObject } from 'yup';
import { useTranslation } from '../../../../../../services/translation/hooks/useTranslation';
import LoadingSpinner from '../../../../../commonUI/LoadingSpinner';
import { darkenColor } from '../../../../../commonUtils';
import { useDebouncedFormikErrorsLog } from '../../../hooks/useDebouncedFormikErrorsLog';
import { SubmitStatuses } from '../../context/CRUDContext';

export interface SubmitButtonConfig {
  sx?: SxProps<Theme>;
  title?: string;
}

export interface SubmitButtonProps<T extends AnyObject> {
  formikProps: FormikProps<T>;
  config?: SubmitButtonConfig;
  sx?: SxProps<Theme>;
  disabled?: boolean;
}

export function SubmitButton<T extends AnyObject>({
  formikProps,
  config,
  sx,
  disabled = false,
}: SubmitButtonProps<Partial<T>>) {
  const theme = useTheme();
  const { t } = useTranslation();
  useDebouncedFormikErrorsLog();
  const [bgColor, setBgColor] = useState<string>(theme.colors.secondary);
  const title = config?.title || t('Submit', { ns: 'common' });

  const status: SubmitStatuses | undefined = formikProps?.status?.status;

  const statusIcon = useMemo(() => {
    if (formikProps.isSubmitting || status === SubmitStatuses.pending) {
      setBgColor(theme.colors.secondary);
      return <LoadingSpinner size='20px' color='white' />;
    }
    if (status === SubmitStatuses.success) {
      setBgColor(theme.colors.status.green.main);
      return <DoneIcon />;
    }

    if (status === SubmitStatuses.error) {
      setBgColor(theme.colors.status.red.main);
      return <WarningIcon />;
    }

    if (status === SubmitStatuses.idle) {
      setBgColor(theme.colors.secondary);
      return undefined;
    }

    return undefined;
  }, [status, formikProps.isSubmitting]);

  return (
    <Button
      type='submit'
      variant='contained'
      sx={
        {
          width: '100%',
          height: '36.5px',
          bgcolor: bgColor,
          '&:hover': {
            bgcolor: darkenColor(bgColor, 0.2),
          },
          '&.Mui-disabled': {
            color: (theme) => theme.colors.common.white,
            bgcolor: bgColor,
            opacity: 0.6,
          },
          ...sx,
          ...config?.sx,
        } as SxProps<Theme>
      }
      onClick={formikProps.submitForm}
      disabled={
        disabled
          ? disabled
          : formikProps.isSubmitting ||
            !formikProps.isValid ||
            status === SubmitStatuses.pending ||
            status === SubmitStatuses.success
      }
    >
      {statusIcon || title}
    </Button>
  );
}
